/**
 * SignatureDialog.jsx (PR_RUN_041 — Module 24)
 * =============================================
 * Polished modal for applying a CFR Part 11 §11.50 / §11.70 / §11.200
 * compliant electronic signature.
 *
 * Replaces the previous inline 3-field form on ESignaturesPage.js with a
 * full-screen-aware dialog that:
 *   • Shows the document context (Document ID + readable title).
 *   • Displays the frozen meaning manifestation in the user's locale BEFORE
 *     signing (CFR §11.70 — what you see is what gets hashed into the row).
 *   • Filters the meaning list to only those the caller is RBAC-permitted
 *     to sign with (server-side filter via /api/esignatures/meanings).
 *   • Accepts optional `comments` (free-text — folded into the signature
 *     audit row for §11.50 traceability).
 *   • Surfaces a re-authentication password field (CFR §11.200(a)(1) —
 *     non-biometric e-sig requires identification code + password; the
 *     password is posted as `password_confirmation`).
 *   • Shows a success card with the assigned `signature_id` on completion.
 *
 * Props:
 *   open                : bool       — controls visibility
 *   onClose             : () => void — close handler
 *   documentId          : string?    — pre-populated doc id (optional)
 *   documentTitle       : string?    — human-readable title (optional)
 *   onSigned            : (sig) => void — fired after successful sign (sig
 *                                          payload returned by the backend)
 *
 * Authored: 2026-05-23 (E1, PR_RUN_041)
 */
import { useEffect, useState } from "react";
import {
  PenLine, Shield, X, FileText, Lock, CheckCircle2, AlertTriangle, Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SignatureDialog = ({
  open,
  onClose,
  documentId = "",
  documentTitle = "",
  onSigned = () => {},
}) => {
  const [meanings, setMeanings] = useState([]);
  const [meaning, setMeaning] = useState("");
  const [docId, setDocId] = useState(documentId);
  const [comments, setComments] = useState("");
  const [password, setPassword] = useState("");
  const [meaningsLoading, setMeaningsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setDocId(documentId || "");
      setMeaning("");
      setComments("");
      setPassword("");
      setError(null);
      setSuccess(null);
    }
  }, [open, documentId]);

  // Fetch role-filtered meanings each time the dialog opens
  useEffect(() => {
    if (!open) return;
    setMeaningsLoading(true);
    fetch(`${API}/api/esignatures/meanings`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((j) => { setMeanings(j?.meanings || []); })
      .finally(() => setMeaningsLoading(false));
  }, [open]);

  if (!open) return null;

  const handleSign = async () => {
    if (!docId.trim() || !meaning) return;
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        document_id: docId.trim(),
        meaning,
        comments: comments.trim() || undefined,
      };
      if (password.trim()) body.password_confirmation = password;
      const r = await fetch(`${API}/api/esignatures/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const errMsg =
          data?.detail?.messages?.en ||
          (typeof data?.detail === "string" ? data.detail : null) ||
          data?.detail?.error ||
          `Error signing (HTTP ${r.status})`;
        setError(errMsg);
        return;
      }
      setSuccess(data);
      onSigned(data);
    } catch (e) {
      setError(String(e));
    } finally {
      setSubmitting(false);
    }
  };

  // Parse "approval — Authorize and approve document for use" → {key, manifest}
  const parseMeaning = (m) => {
    const [key, ...rest] = m.split(" — ");
    return { key, manifest: rest.join(" — ") };
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !submitting) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
      onClick={handleBackdropClick}
      data-testid="signature-dialog"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col"
        style={{ boxShadow: "0 0 0 1px rgba(201, 168, 76, 0.15)" }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-[rgba(255,255,255,0.07)] flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[rgba(201,168,76,0.12)] flex items-center justify-center">
              <PenLine className="w-4 h-4 text-[#C9A84C]" />
            </div>
            <div>
              <h3 className="text-white font-semibold flex items-center gap-2">
                Apply Electronic Signature
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(56,189,248,0.1)] text-[#38BDF8] border border-[rgba(56,189,248,0.2)] flex items-center gap-1">
                  <Shield className="w-2.5 h-2.5" /> CFR Part 11 §11.50
                </span>
              </h3>
              <p className="text-[11px] text-[#8B95A5] mt-0.5">
                Your signature will be folded into the document&apos;s hash chain and audited under your account.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-[#8B95A5] hover:text-white disabled:opacity-40"
            aria-label="Close"
            data-testid="signature-dialog-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          {success ? (
            <div className="text-center py-6" data-testid="signature-dialog-success">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-3" />
              <h4 className="text-white font-semibold mb-1">Signature applied</h4>
              <p className="text-[12px] text-[#8B95A5] mb-3">
                Stored under signature ID
              </p>
              <div className="inline-block px-3 py-1.5 rounded bg-[#0F1217] border border-[rgba(201,168,76,0.25)] font-mono text-[12px] text-[#C9A84C]">
                {success.signature_id}
              </div>
              <p className="text-[11px] text-[#8B95A5] mt-3">
                {success.meaning_signed_text || success.meaning}
              </p>
            </div>
          ) : (
            <>
              {/* Document */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#8B95A5] flex items-center gap-1 mb-1.5">
                  <FileText className="w-3 h-3" /> Document
                </label>
                {documentTitle && (
                  <p className="text-white text-sm mb-1">{documentTitle}</p>
                )}
                <input
                  type="text"
                  value={docId}
                  onChange={(e) => setDocId(e.target.value)}
                  placeholder="Document ID (e.g. DOC-…)"
                  className="w-full px-3 py-2 rounded bg-[#0F1217] border border-[rgba(255,255,255,0.07)] text-[13px] text-white focus:outline-none focus:border-[#C9A84C] font-mono"
                  data-testid="signature-dialog-doc-id"
                  disabled={submitting || Boolean(documentId)}
                />
              </div>

              {/* Meaning */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#8B95A5] mb-1.5 block">
                  Meaning (CFR §11.50)
                </label>
                {meaningsLoading ? (
                  <div className="flex items-center gap-2 text-[#8B95A5] text-xs">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading permitted meanings…
                  </div>
                ) : meanings.length === 0 ? (
                  <div className="text-amber-400 text-xs flex items-center gap-2">
                    <AlertTriangle className="w-3 h-3" />
                    Your role has no permitted signature meanings.
                  </div>
                ) : (
                  <div className="space-y-1.5" role="radiogroup">
                    {meanings.map((m) => {
                      const { key, manifest } = parseMeaning(m);
                      const selected = meaning === m;
                      return (
                        <label
                          key={m}
                          className={`flex items-start gap-3 p-2.5 rounded border cursor-pointer transition-colors ${
                            selected
                              ? "border-[#C9A84C] bg-[rgba(201,168,76,0.06)]"
                              : "border-[rgba(255,255,255,0.07)] hover:border-[rgba(255,255,255,0.15)]"
                          }`}
                          data-testid={`signature-dialog-meaning-${key}`}
                        >
                          <input
                            type="radio"
                            name="meaning"
                            value={m}
                            checked={selected}
                            onChange={(e) => setMeaning(e.target.value)}
                            className="mt-0.5 accent-[#C9A84C]"
                            disabled={submitting}
                          />
                          <div className="flex-1">
                            <div className="text-white text-[13px] font-medium capitalize">{key}</div>
                            <div className="text-[#8B95A5] text-[11px]">{manifest}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Comments */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#8B95A5] mb-1.5 block">
                  Comments <span className="text-[#8B95A5]/60">(optional)</span>
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Context to attach to this signature (e.g. exception rationale, deviation linkage)"
                  rows={2}
                  className="w-full px-3 py-2 rounded bg-[#0F1217] border border-[rgba(255,255,255,0.07)] text-[12px] text-slate-200 focus:outline-none focus:border-[#C9A84C] resize-none"
                  data-testid="signature-dialog-comments"
                  disabled={submitting}
                />
              </div>

              {/* Re-auth */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#8B95A5] mb-1.5 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Re-authenticate <span className="text-[#8B95A5]/60 normal-case tracking-normal">(CFR §11.200)</span>
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password (sent as password_confirmation)"
                  className="w-full px-3 py-2 rounded bg-[#0F1217] border border-[rgba(255,255,255,0.07)] text-[13px] text-white focus:outline-none focus:border-[#C9A84C]"
                  data-testid="signature-dialog-password"
                  disabled={submitting}
                  autoComplete="current-password"
                />
              </div>

              {/* Error banner */}
              {error && (
                <div className="px-3 py-2 rounded bg-[rgba(248,113,113,0.08)] border border-red-500/30 flex items-start gap-2" data-testid="signature-dialog-error">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-red-300">{error}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-[rgba(255,255,255,0.07)] flex justify-end gap-2">
          {success ? (
            <button
              onClick={onClose}
              className="px-4 py-1.5 rounded text-xs font-medium text-white bg-[#C9A84C] hover:bg-[#B8973B]"
              data-testid="signature-dialog-done"
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={submitting}
                className="px-3 py-1.5 rounded text-xs text-[#8B95A5] border border-[rgba(255,255,255,0.07)] hover:text-white disabled:opacity-40"
                data-testid="signature-dialog-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleSign}
                disabled={submitting || !docId.trim() || !meaning}
                className="px-4 py-1.5 rounded text-xs font-medium text-white bg-[#C9A84C] hover:bg-[#B8973B] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                data-testid="signature-dialog-sign"
              >
                {submitting ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /> Signing…</>
                ) : (
                  <><PenLine className="w-3 h-3" /> Sign document</>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignatureDialog;
